const SIGN_GLYPHS = ["\u2648","\u2649","\u264A","\u264B","\u264C","\u264D","\u264E","\u264F","\u2650","\u2651","\u2652","\u2653"];
const SIGNS = ["Aries","Taurus","Gemini","Cancer","Leo","Virgo","Libra","Scorpio","Sagittarius","Capricorn","Aquarius","Pisces"];
const PLANET_GLYPH = {
  Sun: "\u2609", Moon: "\u263D", Mercury: "\u263F", Venus: "\u2640", Mars: "\u2642",
  Jupiter: "\u2643", Saturn: "\u2644", Uranus: "\u2645", Neptune: "\u2646", Pluto: "\u2647",
  Rahu: "\u260A", Ketu: "\u260B",
};

function angDiff(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

// Stagger clustered planets inward so glyphs never overlap; keep the true-longitude tick fixed.
function layout(planets, baseR, step, ascLon) {
  const sorted = [...planets].map((p, idx) => ({ ...p, idx }))
    .sort((a, b) => ((a.longitude - ascLon + 360) % 360) - ((b.longitude - ascLon + 360) % 360));
  let level = 0;
  let prev = null;
  const placed = {};
  for (const p of sorted) {
    if (prev !== null && angDiff(p.longitude, prev) < 8) level = Math.min(level + 1, 3);
    else level = 0;
    placed[p.planet] = { ...p, r: baseR - level * step };
    prev = p.longitude;
  }
  return planets.map((p) => placed[p.planet]);
}

export function ZodiacWheel({
  planets, ascendant, accent = "#45F0DF", size = 340,
  aspects = [], showDegrees = false, angles = null, showHouses = false,
  outerPlanets = null, outerAscendant = null, outerAccent = "#D4AF37",
}) {
  const cx = size / 2, cy = size / 2;
  const rOuter = size / 2 - 4;
  const rSignIn = rOuter - 28;
  const rBaseA = rSignIn - (outerPlanets ? 38 : 20);
  const rBaseB = rSignIn - 14;
  const rAspect = (outerPlanets ? rBaseA : rBaseA) - 34;
  const step = 15;

  const ascLon = ascendant?.longitude ?? 0;
  const toXY = (lon, r) => {
    const a = (180 - (lon - ascLon)) * (Math.PI / 180);
    return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
  };

  const A = layout(planets || [], rBaseA, step, ascLon);
  const B = outerPlanets ? layout(outerPlanets, rBaseB, step, ascLon) : [];
  const aMap = Object.fromEntries(A.map((p) => [p.planet, p.longitude]));
  const bMap = Object.fromEntries(B.map((p) => [p.planet, p.longitude]));

  const outerStart = outerAscendant ? SIGNS.indexOf(outerAscendant.sign) : null;
  const innerStart = SIGNS.indexOf(ascendant?.sign);

  const renderPlanet = (p, color, isOuter) => {
    const [tx, ty] = toXY(p.longitude, isOuter ? rBaseB : rBaseA); // true position dot
    const [x, y] = toXY(p.longitude, p.r);
    return (
      <g key={(isOuter ? "o" : "") + p.planet}>
        <line x1={tx} y1={ty} x2={x} y2={y} stroke={color} strokeWidth="0.4" opacity="0.4" />
        <circle cx={tx} cy={ty} r="1.3" fill={color} />
        <circle cx={x} cy={y} r={showDegrees ? 10.5 : 9.5} fill="rgba(5,5,10,0.92)" stroke={color} strokeWidth="0.8" />
        <text x={x} y={y} fill={color} fontSize="11.5" textAnchor="middle" dominantBaseline="central">
          {PLANET_GLYPH[p.planet] || p.planet[0]}
        </text>
        {showDegrees && (
          <text x={x} y={y + 15} fill="rgba(255,255,255,0.5)" fontSize="6.5" textAnchor="middle">
            {Math.round(p.degree)}{"\u00b0"}{p.retrograde ? "\u211e" : ""}
          </text>
        )}
      </g>
    );
  };

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-auto">
      <circle cx={cx} cy={cy} r={rOuter} fill="none" stroke="rgba(255,255,255,0.14)" />
      <circle cx={cx} cy={cy} r={rSignIn} fill="none" stroke="rgba(255,255,255,0.10)" />
      <circle cx={cx} cy={cy} r={rAspect} fill="none" stroke="rgba(255,255,255,0.05)" />

      {/* Aspect lines (drawn in the core so they never cover glyphs) */}
      {aspects.map((asp, i) => {
        const lonA = aMap[asp.a];
        const lonB = outerPlanets ? bMap[asp.b] : aMap[asp.b];
        if (lonA == null || lonB == null) return null;
        const [x1, y1] = toXY(lonA, rAspect);
        const [x2, y2] = toXY(lonB, rAspect);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={asp.color} strokeWidth="0.9" opacity="0.5" />;
      })}

      {/* Sign ring + house numbers */}
      {SIGNS.map((_, i) => {
        const lonStart = i * 30;
        const [x1, y1] = toXY(lonStart, rSignIn);
        const [x2, y2] = toXY(lonStart, rOuter);
        const [gx, gy] = toXY(lonStart + 15, (rOuter + rSignIn) / 2);
        const aHouse = ((i - innerStart + 12) % 12) + 1;
        const bHouse = outerStart != null ? ((i - outerStart + 12) % 12) + 1 : null;
        const [hx, hy] = toXY(lonStart + 15, rSignIn - 8);
        return (
          <g key={i}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,0.12)" />
            <text x={gx} y={gy} fill="#D4AF37" fontSize="12" textAnchor="middle" dominantBaseline="central">{SIGN_GLYPHS[i]}</text>
            {showHouses && (
              <text x={hx} y={hy} fill={outerStart != null ? "#fff" : accent} fontSize="7" textAnchor="middle" dominantBaseline="central" opacity="0.6">
                {outerStart != null ? `${aHouse}·${bHouse}` : aHouse}
              </text>
            )}
          </g>
        );
      })}

      {/* Angles */}
      {angles && ["Ascendant", "Midheaven", "Descendant", "IC"].map((k) => {
        const g = angles[k];
        if (!g) return null;
        const [x1, y1] = toXY(g.longitude, rAspect);
        const [x2, y2] = toXY(g.longitude, rSignIn);
        const [lx, ly] = toXY(g.longitude, rSignIn - 16);
        const lbl = { Ascendant: "AC", Midheaven: "MC", Descendant: "DC", IC: "IC" }[k];
        return (
          <g key={k}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={accent} strokeWidth="0.6" strokeDasharray="2 2" opacity="0.7" />
            <text x={lx} y={ly} fill={accent} fontSize="8" fontWeight="700" textAnchor="middle" dominantBaseline="central">{lbl}</text>
          </g>
        );
      })}
      {!angles && ascendant && (() => {
        const [x, y] = toXY(ascLon, rSignIn - 16);
        return <text x={x} y={y} fill={accent} fontSize="9" fontWeight="700" textAnchor="middle" dominantBaseline="central">AC</text>;
      })()}

      {A.map((p) => renderPlanet(p, accent, false))}
      {B.map((p) => renderPlanet(p, outerAccent, true))}
    </svg>
  );
}

export { PLANET_GLYPH };
